import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { ConciliacaoService } from '../../../services/conciliacao.service';
import { ResumoBuscaEmMassa } from '../../../models/resumo-busca-em-massa';
import { Lancamento } from '../../../models/lancamento';
import { OrigemLancamento } from '../../../../../../shared/models/enums/origem-lancamento';
import { ListagemBuscaEmMassaDialogComponent } from '../listagem-busca-em-massa-dialog/listagem-busca-em-massa-dialog.component';
import { MessageService } from '@services/message.service';

@Component({
  selector: 'app-busca-em-massa-dialog',
  standalone: false,
  templateUrl: './busca-em-massa-dialog.component.html',
  styleUrl: './busca-em-massa-dialog.component.scss'
})
export class BuscaEmMassaDialogComponent implements OnInit, OnDestroy {

  processando: boolean = true;
  processados: number = 0;
  total: number = 0;
  resumo: ResumoBuscaEmMassa | null = null;

  // null enquanto roda; setado no clique de um dos dois botões de parada — controla tanto o
  // desabilitar deles quanto (via deveDescartarResultados) o que acontece ao parar. "Interromper"
  // concilia o que já foi buscado até ali; "Cancelar" (e o X, que dispara o mesmo fluxo via
  // ngOnDestroy) descarta tudo e não concilia nada.
  acaoParada: 'interromper' | 'cancelar' | null = null;

  // Contagem regressiva do tempo limite de busca (tempoLimiteBusca) do lançamento atual.
  segundosRestantes: number = 0;
  private timerId: any = null;

  get percentualConcluido(): number {
    return this.total === 0 ? 0 : Math.round((this.processados / this.total) * 100);
  }

  get parando(): boolean {
    return this.acaoParada !== null;
  }

  constructor(
    private conciliacaoService: ConciliacaoService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.conciliacaoService
      .executarBuscaEmMassa(
        (processados, total) => this.onProgresso(processados, total),
        () => this.acaoParada !== null,
        () => this.acaoParada === 'cancelar',
        () => this.iniciarContagemRegressiva()
      )
      .then(resumo => this.onConcluido(resumo))
      .catch(() => this.onErro());
  }

  public interromperClick(): void {
    this.pararProcessamento('interromper');
  }

  public cancelarClick(): void {
    this.pararProcessamento('cancelar');
  }

  // O X padrão do smrts-dialog (e qualquer outra forma de fechar o dialog) não passa pelos cliques
  // acima — sem isso, fechar o dialog durante o processamento deixaria a busca em massa rodando
  // "invisível" em segundo plano, sem chance de ser interrompida. Mesmo comportamento do botão
  // "Cancelar": descarta o que já foi buscado, não concilia nada.
  ngOnDestroy(): void {
    if (this.processando) this.pararProcessamento('cancelar');
    this.pararContagemRegressiva();
  }

  private pararProcessamento(acao: 'interromper' | 'cancelar'): void {
    if (this.acaoParada !== null) return; // já solicitado (ex.: clique seguido pelo ngOnDestroy do fechamento)

    this.acaoParada = acao;
    // Sem isso, a busca do lançamento atual continuaria até terminar (ou até estourar o tempo
    // limite) antes do loop notar o pedido de parada — interrompe o worker imediatamente.
    this.conciliacaoService.interromperBuscaEmAndamento();
  }

  // Reinicia a contagem a cada novo lançamento — é apenas uma exibição em paralelo ao setTimeout
  // real do service (executarBuscaNoWorker), não o controla; existe só para dar visibilidade ao
  // usuário de quanto falta para aquele lançamento específico estourar o tempo limite de busca.
  private iniciarContagemRegressiva(): void {
    this.pararContagemRegressiva();

    const tempoLimiteBusca = this.conciliacaoService.tempoLimiteBusca;
    if (tempoLimiteBusca <= 0) return; // 0 = sem tempo limite configurado

    this.segundosRestantes = tempoLimiteBusca;
    this.timerId = setInterval(() => {
      this.segundosRestantes = Math.max(0, this.segundosRestantes - 1);
      if (this.segundosRestantes === 0) this.pararContagemRegressiva();
    }, 1000);
  }

  private pararContagemRegressiva(): void {
    if (this.timerId === null) return;

    clearInterval(this.timerId);
    this.timerId = null;
  }

  public existeItemParaRevisao(resumo: ResumoBuscaEmMassa): boolean {
    return resumo.semCorrespondencia > 0 || resumo.comMultiplasCorrespondencias > 0
      || resumo.comConflitoEntreLancamentos > 0 || resumo.comTimeout > 0;
  }

  // Abre a listagem por cima do dialog atual (novo dialog empilhado, sem fechar este).
  // comCorrespondencia só é true para a lista de conciliados, habilitando o botão "Ver correspondência".
  public abrirListagem(titulo: string, lancamentos: Lancamento[], comCorrespondencia: boolean = false): void {
    this.dialog.open(ListagemBuscaEmMassaDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      autoFocus: false,
      data: {
        titulo,
        lancamentos,
        origem: this.resumo!.origemProcessamento,
        cardEhBanco: comCorrespondencia ? this.resumo!.origemProcessamento === OrigemLancamento.Banco : undefined,
      },
    });
  }

  private onProgresso(processados: number, total: number): void {
    this.processados = processados;
    this.total = total;
  }

  private onConcluido(resumo: ResumoBuscaEmMassa): void {
    this.pararContagemRegressiva();
    this.processando = false;
    this.resumo = resumo;
    this.messageService.showToastSuccess(this.getResumoToast(resumo));
  }

  private onErro(): void {
    this.pararContagemRegressiva();
    this.processando = false;
    this.messageService.showToastError('Ocorreu um erro durante a busca em massa.');
  }

  private getResumoToast(resumo: ResumoBuscaEmMassa): string {
    // Cancelar/X descartam tudo o que foi buscado — sempre 0 conciliados, não há o que informar.
    if (this.acaoParada === 'cancelar')
      return 'Busca em massa cancelada.';

    if (resumo.cancelado)
      return `Busca em massa interrompida. ${resumo.conciliadosAutomaticamente} lançamento(s) conciliado(s) antes da interrupção.`;

    return `Busca em massa concluída: ${resumo.conciliadosAutomaticamente} lançamento(s) conciliado(s) automaticamente.`;
  }
}
